import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { cn } from '../lib/cn.js';
import { AnchoredList, useAnchor } from './anchored-list.js';

/**
 * One bar for a scanner and a keyboard both.
 *
 * A counter billing out of a warehouse has two ways of naming an item and no
 * spare hand for choosing between them: the label under the gun, or what the
 * operator can remember of the SKU, the item, the brand, the lot number. Two
 * boxes — "barcode" and "search" — make that a decision before any work starts,
 * and the decision is always the same one, so it is not a decision, it is a
 * tax. This is one box.
 *
 * **Telling a scan from typing is a timing question, not a format question.** A
 * wedge scanner is a keyboard that types a whole code in a few milliseconds and
 * presses Enter; a person types at human speed and pauses. So the bar debounces
 * ordinary typing and shows a list, but an `Enter` — or a burst that arrives
 * faster than a person can type — resolves immediately and, when the answer is
 * certain, never opens a list at all. `SCAN_SPEED_MS` is the threshold, and it
 * is deliberately loose: guessing wrong costs one dropdown, not a wrong line.
 *
 * **What the bar never does is lose focus.** After a scan it clears and stays
 * where it is, because the next thing that happens at a counter is another
 * scan. Everything else on the screen is reachable by Tab; this is the one
 * control that has to be there without being asked for.
 *
 * The bar knows nothing about invoices. It resolves and reports; the screen
 * decides what a resolved item means — a new line, a bumped quantity, a
 * refusal because the warehouse is empty.
 */

/**
 * Keystrokes closer together than this came from a machine.
 *
 * Sustained human typing tops out around 8 characters a second — roughly 125ms
 * between keys — and a wedge scanner is an order of magnitude faster than that.
 */
const SCAN_SPEED_MS = 34;

/** How long to wait after the last keystroke before asking the server. */
const DEBOUNCE_MS = 130;

/** How long to let a machine-speed burst finish arriving. */
const SCAN_SETTLE_MS = 45;

/** Below this many characters a lookup is more noise than help. */
const MIN_QUERY = 2;

export interface ScanHit {
  sku: string;
  productName: string;
  brand?: string | null;
  baseUom: string;
  saleUom: string;
  scanUom: string;
  packSizeBase: string | null;
  /** Base units one scan is worth — a 50 Kg bag label is `'50'`. */
  qtyPerScan: string;
  /** The same scan expressed in `scanUom`, computed in the database. */
  scanQty: string;
  /** Base units in one `scanUom` — how a quantity is compared against stock. */
  baseFactor: string;
  warehouseCode: string | null;
  balanceQty: string;
  availableQty: string;
  movingAvgRate: string;
  batchNo: string | null;
  expiryDate: string | null;
  batchQty: string | null;
  daysToExpiry: number | null;
  unitRate: string | null;
  salesTaxPct: string | null;
  trackBatch: boolean;
  trackExpiry: boolean;
  matchKind: 'BARCODE' | 'SKU_EXACT' | 'SKU' | 'BATCH' | 'NAME';
}

export interface ScanBarProps {
  /**
   * Resolve a query. Returns the certain match, if there was one, and the list
   * to choose from otherwise. The screen supplies this so the bar has no
   * opinion about how the application talks to its server.
   */
  lookup: (query: string) => Promise<{ exact: ScanHit | null; hits: ScanHit[] }>;
  /** A resolved item — from a scan, or from a row the operator picked. */
  onPick: (hit: ScanHit) => void;
  placeholder?: string;
  /** Shown under the bar: the running line count, the last item added. */
  status?: React.ReactNode;
  disabled?: boolean;
  /** Why the bar cannot be used yet — "choose a customer first". */
  disabledReason?: string;
  className?: string;
  'aria-label'?: string;
}

export function ScanBar({
  lookup,
  onPick,
  placeholder = 'Scan a barcode, or type a SKU, item, brand or batch',
  status,
  disabled,
  disabledReason,
  className,
  'aria-label': ariaLabel = 'Scan or search for an item',
}: ScanBarProps) {
  const listId = useId();
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<ScanHit[]>([]);
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [missed, setMissed] = useState<string | null>(null);

  const { ref: inputRef, anchor } = useAnchor(open);
  const listRef = useRef<HTMLDivElement>(null);

  /** When the previous keystroke landed — the scanner/human discriminator. */
  const lastKeyAt = useRef(0);
  const machineTyped = useRef(false);
  /** Only the newest lookup may write state; a slow one must not overwrite it. */
  const generation = useRef(0);

  const reset = useCallback(() => {
    setQuery('');
    setHits([]);
    setActive(0);
    setOpen(false);
    machineTyped.current = false;
  }, []);

  const take = useCallback(
    (hit: ScanHit) => {
      onPick(hit);
      setMissed(null);
      reset();
      inputRef.current?.focus();
    },
    [onPick, reset, inputRef],
  );

  const resolve = useCallback(
    async (text: string, immediate: boolean) => {
      const term = text.trim();
      if (term.length < (immediate ? 1 : MIN_QUERY)) {
        setHits([]);
        setOpen(false);
        return;
      }
      const mine = ++generation.current;
      setBusy(true);
      try {
        const result = await lookup(term);
        if (mine !== generation.current) return;

        // A certain match is not a list. `exact` is only ever a barcode or a
        // whole SKU, and pausing to confirm one of those is the interruption
        // this bar exists to remove.
        if (result.exact) {
          take(result.exact);
          return;
        }
        setHits(result.hits);
        setActive(0);
        setOpen(result.hits.length > 0);
        // Silence unless the operator committed. Half a code typed matches
        // nothing yet and saying so on every keystroke is nagging.
        if (immediate) setMissed(result.hits.length === 0 ? term : null);
      } catch {
        if (mine === generation.current) {
          setHits([]);
          setOpen(false);
        }
      } finally {
        if (mine === generation.current) setBusy(false);
      }
    },
    [lookup, take],
  );

  /**
   * Two waits, one code path.
   *
   * A person gets the full debounce, because they are still deciding and a list
   * that re-sorts under them on every keystroke is worse than a short pause. A
   * machine-speed burst gets a much shorter settle — just long enough for the
   * rest of the code to land — and is then treated as a committed scan, so an
   * exact match adds its line without waiting for a terminator.
   *
   * The distinction changes how long we wait, never *whether* we ask. Scanners
   * are configurable and plenty are set to send no suffix at all; a branch that
   * only resolved on Enter would leave those silently dead.
   */
  useEffect(() => {
    if (query.trim().length < MIN_QUERY) {
      setHits([]);
      setOpen(false);
      return;
    }
    const scanned = machineTyped.current;
    const timer = setTimeout(
      () => void resolve(query, scanned),
      scanned ? SCAN_SETTLE_MS : DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [query, resolve]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const now = Date.now();
    if (event.key.length === 1) {
      // Re-decided on every character rather than latched: a burst that turns
      // into ordinary typing — somebody correcting a scan by hand — should get
      // the human treatment back, and a scanner re-latches on its second key.
      machineTyped.current = now - lastKeyAt.current < SCAN_SPEED_MS;
      lastKeyAt.current = now;
    }

    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        // Enter with a highlighted row takes it; Enter on raw text is a scanner
        // finishing, or an operator saying "this is the whole thing".
        if (open && hits[active]) take(hits[active]);
        else void resolve(query, true);
        break;
      case 'ArrowDown':
        if (hits.length === 0) return;
        event.preventDefault();
        setOpen(true);
        setActive((i) => Math.min(i + 1, hits.length - 1));
        break;
      case 'ArrowUp':
        if (hits.length === 0) return;
        event.preventDefault();
        setActive((i) => Math.max(i - 1, 0));
        break;
      case 'Escape':
        /*
          Escape clears the bar — but only while there is something in it to
          clear, and it is swallowed only then.

          It used to `stopPropagation` unconditionally, which made the bar a
          keyboard trap for everything above it: the entry panel that hosts this
          closes on Escape, and with the caret sitting in an empty scan bar —
          which is where it sits, because this is the control the screen focuses
          — that Escape went nowhere and the panel could not be closed from the
          keyboard at all. `Combobox` has always had this right, and this is the
          same rule: a control may take a key only when it has a use for it.
        */
        if (query === '' && hits.length === 0 && missed === null) return;
        event.preventDefault();
        event.stopPropagation();
        reset();
        setMissed(null);
        break;
      default:
        break;
    }
  };

  // Keep the highlight in view while arrowing a long list.
  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  return (
    <div className={cn('flex flex-col gap-1.5', className)} data-noprint="1">
      <div className="relative flex items-center">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-3.5 text-[15px] text-[var(--ink-3)]"
        >
          ⌗
        </span>
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          autoFocus
          disabled={disabled}
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-autocomplete="list"
          value={query}
          placeholder={disabled ? (disabledReason ?? placeholder) : placeholder}
          onChange={(event) => {
            setQuery(event.target.value);
            if (missed) setMissed(null);
          }}
          onKeyDown={onKeyDown}
          onBlur={(event) => {
            if (listRef.current?.contains(event.relatedTarget as Node)) return;
            setOpen(false);
          }}
          className={cn(
            'h-11 w-full rounded-[var(--radius)] border bg-[var(--surface-2)] pr-24 pl-9',
            'text-[14px] text-[var(--ink)] outline-none',
            'placeholder:text-[var(--ink-3)]',
            'transition-[border-color,box-shadow] duration-[var(--dur-fast)]',
            'focus:border-[var(--accent)] focus:bg-[var(--surface)] focus:ring-2 focus:ring-[var(--accent-soft)]',
            'disabled:cursor-not-allowed disabled:text-[var(--ink-3)]',
            missed ? 'border-[var(--warn)]' : 'border-[var(--line)]',
          )}
        />
        <span className="pointer-events-none absolute right-3.5 text-[11px] text-[var(--ink-3)]">
          {busy ? 'looking…' : 'Enter'}
        </span>
      </div>

      {missed && (
        <p className="m-0 text-[11.5px] text-[var(--warn)]">
          Nothing matches <span className="bb-mono">{missed}</span> — no barcode, SKU, batch or item
          name. Check the warehouse, or add the label on Product Management.
        </p>
      )}
      {!missed && status && <p className="m-0 text-[11.5px] text-[var(--ink-3)]">{status}</p>}

      {open && anchor && (
        <AnchoredList anchor={anchor} id={listId} listRef={listRef} minWidth={360}>
          {hits.map((hit, index) => {
            const empty = Number(hit.availableQty) <= 0;
            const soon = hit.daysToExpiry !== null && hit.daysToExpiry < 30;
            return (
              <div
                key={`${hit.sku} ${hit.batchNo ?? ''} ${index}`}
                role="option"
                data-index={index}
                aria-selected={index === active}
                aria-disabled={empty || undefined}
                onMouseEnter={() => setActive(index)}
                onClick={() => !empty && take(hit)}
                className={cn(
                  'flex items-baseline gap-2.5 px-3 py-2 text-[12.5px]',
                  empty ? 'cursor-not-allowed opacity-55' : 'cursor-pointer',
                  index === active && !empty && 'bg-[var(--accent-soft)]',
                )}
              >
                <span className="bb-mono shrink-0 text-[12px] text-[var(--accent)]">{hit.sku}</span>
                <span className="min-w-0 flex-1 truncate text-[var(--ink)]">
                  {hit.productName}
                  {hit.batchNo && (
                    <span
                      className={cn(
                        'bb-mono pl-2 text-[11px]',
                        soon ? 'text-[var(--warn)]' : 'text-[var(--ink-3)]',
                      )}
                    >
                      {hit.batchNo}
                      {hit.expiryDate ? ` · exp ${hit.expiryDate}` : ''}
                      {soon ? ` · ${hit.daysToExpiry}d` : ''}
                    </span>
                  )}
                </span>
                <span
                  className={cn(
                    'bb-num shrink-0 text-[11.5px] tabular-nums',
                    empty ? 'text-[var(--debit)]' : 'text-[var(--ink-3)]',
                  )}
                >
                  {empty ? 'none' : `${Number(hit.availableQty).toLocaleString()} ${hit.baseUom}`}
                </span>
              </div>
            );
          })}
        </AnchoredList>
      )}
    </div>
  );
}
